import { Box, Circle, Table, Tag, Text, colors } from '@factorialco/deprecated-design-system'
import { WorkScheduleOverlapPeriodFragment } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateRange } from 'modules/core/lib/dates'
import { weekdaysWithUserFirstDayOfWeek } from 'modules/core/lib/firstDayOfWeek'
import { formatSecondsToHM } from 'modules/core/lib/formatTime'

import DayConfigRowBreaks from './DayConfigRowBreaks'
import EmptyScheduleDay from './EmptyScheduleDay'
import type { DayConfigurationWithBreaks } from './types'

interface Props {
  dayConfigsPerPeriod: DayConfigurationWithBreaks[]
  period: WorkScheduleOverlapPeriodFragment
}

const TableRowFlexibleSchedule = ({ dayConfigsPerPeriod, period }: Props) => {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const startDate = period.startDate
  const endDate = period.endDate
  const isDefault = period.default
  const periodRange = dateRange(startDate, endDate)

  const dayConfigsByDay =
    dayConfigsPerPeriod &&
    groupBy(dayConfigsPerPeriod, ({ dayConfiguration }) => dayConfiguration.weekday)

  // Flexible WorkSchedules could only have one DayConfiguration per Day
  const durationInSecondsPerDay = weekdaysWithUserFirstDayOfWeek(firstDayOfWeek).map((day) => {
    const configsByDay = dayConfigsByDay[day]

    if (!configsByDay) return { seconds: 0, breaks: [] }

    const config = configsByDay[0]

    if (!config) return { seconds: 0, breaks: [] }

    return {
      seconds: configsByDay[0]?.dayConfiguration.durationInSeconds || 0,
      breaks: config.breaks,
    }
  })

  return (
    <Table.Row>
      <Table.Cell verticalAlign="top">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Circle color={isDefault ? colors.viridian1000 : colors.champagne1000} size={8} />
          <Text.Normal color="grey1000">{periodRange}</Text.Normal>
        </Box>
      </Table.Cell>
      {durationInSecondsPerDay.map(({ seconds, breaks }, index) => {
        return (
          <Table.Cell wide key={index} verticalAlign="top" paddingX="s8">
            <Box gap="s8">
              <Box width="full" gap="s16">
                {!seconds ? (
                  <Box width="s140" justifyContent="center">
                    <EmptyScheduleDay />
                  </Box>
                ) : (
                  <Box width="s140" justifyContent="center" gap="s8">
                    <Tag type="grey">{`${formatSecondsToHM(seconds)}`}</Tag>
                    <DayConfigRowBreaks plannedBreaks={breaks} />
                  </Box>
                )}
              </Box>
            </Box>
          </Table.Cell>
        )
      })}
    </Table.Row>
  )
}

export default observer(TableRowFlexibleSchedule)
